import { useContext, useCallback } from 'react';
import { ProCalculatorContext } from '../contexts/ProCalculatorContext';
import { 
  convertLength, 
  convertWeight, 
  round, // Import round
  calculateArea, 
  validateAndFormatValue, 
  formatNumber 
} from '../utils/calculatorUtils';

// Hook for Pro Calculator functionality

export const useProCalculator = () => {
  const context = useContext(ProCalculatorContext);
  
  if (!context) {
    throw new Error('useProCalculator must be used within a ProCalculatorProvider');
  }
  
  // Function to update a dimensions row
  const updateDimensionsRow = useCallback((id, field, value) => {
    console.log(`%cuseProCalc: updateDimensionsRow called: id=${id}, field=${field}, value=${value}`, 'color: teal');
    // Call the update function from the context directly
    // This function within the context now handles recalculations and state updates
    if (context.updateDimensionsRow) {
       context.updateDimensionsRow(id, field, value);
    } else {
       console.error("useProCalculator: context.updateDimensionsRow is not available!");
    }
  }, [context.updateDimensionsRow]); // Depend on the context's update function

  // Function to update a color row
  const updateColorRow = (id, field, value) => {
    context.setColorRows(prev => 
      prev.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };
  
  // Function to update a color percentage
  const updateColorPercentage = (rowId, colorIndex, value) => {
    const { colorNames, setColorRows } = context;
    const numColors = colorNames.length;

    setColorRows(prevColorRows => 
      prevColorRows.map(row => {
        if (row.id === rowId) {
          const newValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
          let newPercentages = [...row.percentages];

          if (numColors === 2) {
            // Auto-balance for exactly two colors
            const otherColorIndex = colorIndex === 0 ? 1 : 0;
            const otherValue = Math.max(0, 100 - newValue);
            newPercentages = []; // Create new array
            newPercentages[colorIndex] = newValue;
            newPercentages[otherColorIndex] = otherValue;
          } else {
            // Standard update for 1, 3, or more colors
            newPercentages[colorIndex] = newValue;
            // Ensure the array length matches numColors, adding 0 if needed (might happen during add/remove transitions)
            while (newPercentages.length < numColors) {
                newPercentages.push(0);
            }
             // Trim excess if needed (might happen during remove transitions)
            if (newPercentages.length > numColors) {
                newPercentages = newPercentages.slice(0, numColors);
            }
          }
          
          return { ...row, percentages: newPercentages };
        }
        return row;
      })
    );
  };

  // Function to add a new color
  const addColor = () => {
    const { setColorNames, colorRows, setColorRows } = context;
    let newColorNames = [];

    // Add a new color name
    setColorNames(prev => {
      newColorNames = [...prev, `Color ${prev.length + 1}`];
      return newColorNames;
    });
    
    // Update all color rows to add a percentage for the new color
    setColorRows(prevColorRows => {
      const numColors = newColorNames.length; // Get the NEW total number of colors

      return prevColorRows.map(row => {
        let newPercentages = [...row.percentages];
        
        if (numColors === 1) {
          // First color added, set to 100%
          newPercentages = [100];
        } else if (numColors === 2) {
          // Second color added, set both to 50%
          newPercentages = [50, 50];
        } else {
          // Third or subsequent color added, initialize to 0%
          newPercentages.push(0); // Add 0 for the new color
        }
        
        return { ...row, percentages: newPercentages };
      });
    });
  };

  // Function to remove a color
  const removeColor = (colorIndex) => {
    const { colorNames, setColorNames, colorRows, setColorRows } = context;

    // Remove the color name
    const newColorNames = colorNames.filter((_, index) => index !== colorIndex);
    setColorNames(newColorNames);
    
    // Remove the corresponding percentage from each color row
    setColorRows(prevColorRows => {
      const updatedRows = prevColorRows.map(row => {
        const newPercentages = row.percentages.filter((_, index) => index !== colorIndex);
        return { ...row, percentages: newPercentages };
      });

      // If only one color remains, set its percentage to 100 for all rows
      if (newColorNames.length === 1) {
        return updatedRows.map(row => ({
          ...row,
          percentages: [100] // The only remaining color gets 100%
        }));
      }

      return updatedRows; // Return rows with percentage removed
    });
  };
  
  // Function to update an order row
  const updateOrderRow = (cookieId, count) => {
    context.setOrderRows(prev => {
      // Check if an order row already exists for this cookie
      const existingRow = prev.find(row => row.cookieId === cookieId);
      
      if (existingRow) {
        // Update the existing row
        return prev.map(row => 
          row.cookieId === cookieId ? { ...row, count } : row
        );
      } else {
        // Create a new row
        const newId = prev.length > 0 ? Math.max(...prev.map(row => row.id)) + 1 : 1;
        return [...prev, { id: newId, cookieId, count }];
      }
    });
  };
  
  // Function to calculate color icing amounts for a cookie
  const calculateColorIcing = (cookieId, count = 1) => {
    console.log(`[calculateColorIcing] Called for cookieId: ${cookieId}, count: ${count}`);

    // Find the dimensions row for this cookie
    const dimensionsRow = context.dimensionsRows.find(row => row.cookieId === cookieId);
    console.log(`[calculateColorIcing] Found dimensionsRow:`, dimensionsRow ? JSON.parse(JSON.stringify(dimensionsRow)) : 'NOT FOUND');
    
    // Find the color row for this cookie
    const colorRow = context.colorRows.find(row => row.cookieId === cookieId);
    console.log(`[calculateColorIcing] Found colorRow:`, colorRow ? JSON.parse(JSON.stringify(colorRow)) : 'NOT FOUND');
    
    // If either is missing, return zeros
    if (!dimensionsRow || !colorRow) {
      console.log('[calculateColorIcing] Missing dimensionsRow or colorRow, returning zeros.');
      return Array(context.colorNames.length).fill(0);
    }
    
    // Get the total icing needed for this cookie
    const totalIcingNeeded = parseFloat(dimensionsRow.icingNeeded) || 0;
    console.log(`[calculateColorIcing] totalIcingNeeded (from dimensionsRow.icingNeeded='${dimensionsRow.icingNeeded}'): ${totalIcingNeeded}`);
    
    // Calculate the amount for each color based on percentages
    console.log(`[calculateColorIcing] colorRow.percentages:`, colorRow.percentages);
    const calculatedAmounts = colorRow.percentages.map(percentage => {
      const amount = totalIcingNeeded * (percentage / 100) * count;
      return amount;
    });
    console.log(`[calculateColorIcing] Final calculatedAmounts for cookieId ${cookieId}:`, calculatedAmounts);
    return calculatedAmounts;
  };
  
  // Function to handle unit toggle
  const handleUnitToggle = (e) => {
    // Extract the checked value from the event
    const isMetricNew = e.target.checked;
    
    // Only update if the value is changing
    if (isMetricNew !== context.isMetric) {
      // Update the unit state
      context.setIsMetric(isMetricNew);
      
      // Convert reference cookie dimensions
      if (context.reference.width || context.reference.length) {
        const newWidth = context.reference.width 
          ? (isMetricNew 
              ? convertLength(context.reference.width, true) 
              : convertLength(context.reference.width, false))
          : '';
          
        const newLength = context.reference.length
          ? (isMetricNew
              ? convertLength(context.reference.length, true)
              : convertLength(context.reference.length, false))
          : '';
          
        context.setReference(prev => ({
          ...prev,
          width: newWidth,
          length: newLength
        }));
      }
      
      // Convert dimensions rows
      context.setDimensionsRows(prev => prev.map(row => {
        // Convert each dimension based on the new unit system
        let newWidth = row.width;
        let newLength = row.length;
        let newDiameter = row.diameter;
        
        // If switching to metric (inches to mm), multiply by 25.4
        // If switching to imperial (mm to inches), divide by 25.4
        if (row.width) {
          newWidth = (isMetricNew ? parseFloat(row.width) * 25.4 : parseFloat(row.width) / 25.4).toFixed(2);
        }
        
        if (row.length) {
          newLength = (isMetricNew ? parseFloat(row.length) * 25.4 : parseFloat(row.length) / 25.4).toFixed(2);
        }
        
        if (row.diameter) {
          newDiameter = (isMetricNew ? parseFloat(row.diameter) * 25.4 : parseFloat(row.diameter) / 25.4).toFixed(2);
        }
        
        return { 
          ...row, 
          width: newWidth, 
          length: newLength, 
          diameter: newDiameter 
        };
      }));
    }
  };
  
  // Function to handle weight unit toggle
  const handleWeightUnitToggle = (e) => {
    // DIRECT DEBUG LOG
    console.log('====================');
    console.log('WEIGHT TOGGLE CLICKED');
    console.log('Event:', e);
    console.log('Context methods available:', Object.keys(context));
    console.log('handleReferenceChange exists?', !!context.handleReferenceChange);
    console.log('====================');
    
    // Extract the checked value from the event
    const isMetricWeightNew = e.target.checked;
    
    console.log(`Weight unit toggle: ${context.isMetricWeight ? 'g' : 'oz'} -> ${isMetricWeightNew ? 'g' : 'oz'}`);
    
    // Only update if the value is changing
    if (isMetricWeightNew !== context.isMetricWeight) {
      // Store the current values before toggling
      const currentWeight = context.reference.weight;
      const currentWeightNoIcing = context.reference.weightNoIcing;
      
      console.log('Current values before conversion:', { 
        currentWeight, 
        currentWeightNoIcing, 
        currentUnit: context.isMetricWeight ? 'g' : 'oz' 
      });
      
      // Calculate the new converted values
      let newWeight = '';
      let newWeightNoIcing = '';
      
      if (currentWeight && currentWeight !== '') {
        // If going from oz to g, multiply by GRAMS_PER_OZ
        // If going from g to oz, divide by GRAMS_PER_OZ
        const convertedWeight = isMetricWeightNew 
          ? (context.isMetricWeight ? currentWeight : convertWeight(currentWeight, true))  // to grams
          : (context.isMetricWeight ? convertWeight(currentWeight, false) : currentWeight); // to ounces
        newWeight = round(convertedWeight); // Apply rounding here
      }
      
      if (currentWeightNoIcing && currentWeightNoIcing !== '') {
        const convertedWeightNoIcing = isMetricWeightNew 
          ? (context.isMetricWeight ? currentWeightNoIcing : convertWeight(currentWeightNoIcing, true))  // to grams
          : (context.isMetricWeight ? convertWeight(currentWeightNoIcing, false) : currentWeightNoIcing); // to ounces
        newWeightNoIcing = round(convertedWeightNoIcing); // Apply rounding here
      }
      
      console.log('Converted values (rounded):', { 
        newWeight, 
        newWeightNoIcing, 
        newUnit: isMetricWeightNew ? 'g' : 'oz' 
      });
      
      // DIRECT FIX: Force update the reference values using direct DOM manipulation
      // This is a last resort but should work regardless of React state issues
      console.log('EMERGENCY FIX: Using direct approach to update fields');
      
      // Update the weight unit state first
      context.setIsMetricWeight(isMetricWeightNew);
      
      // Force update the input fields directly after a short delay
      setTimeout(() => {
        try {
          // Get all reference input fields
          const weightInput = document.querySelector('input[value="' + context.reference.weight + '"]');
          const weightNoIcingInput = document.querySelector('input[value="' + context.reference.weightNoIcing + '"]');
          
          // Input fields check
          
          // Update the values directly (using rounded values)
          if (weightInput && newWeight !== '') {
            // Update weight input value
            weightInput.value = newWeight; // Use rounded value
            
            // Trigger change event
            const event = new Event('input', { bubbles: true });
            weightInput.dispatchEvent(event);
          }
          
          if (weightNoIcingInput && newWeightNoIcing !== '') {
            // Update weightNoIcing input value
            weightNoIcingInput.value = newWeightNoIcing; // Use rounded value
            
            // Trigger change event
            const event = new Event('input', { bubbles: true });
            weightNoIcingInput.dispatchEvent(event);
          }
          
          // Also try to update the context directly as a backup (using rounded values)
          context.setReference(prev => ({
            ...prev,
            weight: newWeight || prev.weight, // Use rounded value
            weightNoIcing: newWeightNoIcing || prev.weightNoIcing // Use rounded value
          }));
          
          // Direct update completed
        } catch (error) {
          // Handle error during direct update
        }
      }, 100);
    }
  };
  
  // Function to normalize percentages in each row to 100%
  const normalizeColorPercentages = () => {
    const { colorRows, setColorRows } = context;

    setColorRows(prevColorRows => 
      prevColorRows.map(row => {
        const currentPercentages = row.percentages;
        const sum = currentPercentages.reduce((acc, val) => acc + val, 0);

        // Only normalize if sum is not 0 and not already 100
        if (sum <= 0 || Math.abs(sum - 100) < 0.01) { // Allow for small floating point errors
          return row; // No changes needed
        }

        const scaleFactor = 100 / sum;
        let scaledPercentages = currentPercentages.map(p => p * scaleFactor);

        // Adjust the last percentage slightly to ensure the sum is exactly 100
        // due to potential floating point inaccuracies after scaling.
        const scaledSum = scaledPercentages.reduce((acc, val) => acc + val, 0);
        const difference = 100 - scaledSum;
        
        // Find the largest percentage index to apply the difference for stability
        let largestIndex = 0;
        for (let i = 1; i < scaledPercentages.length; i++) {
          if (scaledPercentages[i] > scaledPercentages[largestIndex]) {
            largestIndex = i;
          }
        }
        scaledPercentages[largestIndex] += difference;

        // Ensure no percentage is negative after adjustment (rare edge case)
        scaledPercentages = scaledPercentages.map(p => Math.max(0, p));
        
        // Re-normalize if adjustment made it go over 100 (due to negative edge case)
        const finalSum = scaledPercentages.reduce((acc, val) => acc + val, 0);
        if(Math.abs(finalSum - 100) > 0.01 && finalSum > 0) {
            const finalScale = 100 / finalSum;
            scaledPercentages = scaledPercentages.map(p => p * finalScale);
             // Final rounding adjustment if needed (usually very minor)
            const finalSum2 = scaledPercentages.reduce((acc, val) => acc + val, 0);
            scaledPercentages[largestIndex] += (100 - finalSum2);
        }

        // Round percentages for display (optional, but cleaner)
        // scaledPercentages = scaledPercentages.map(p => Math.round(p));

        return { ...row, percentages: scaledPercentages.map(p => parseFloat(p.toFixed(2))) }; // Round to 2 decimal places
      })
    );
  };

  // Function to sort dimensions rows alphabetically by cookie name
  const sortDimensionsRows = () => {
    context.setDimensionsRows(prev => {
      // Create a map of cookieId to cookie name
      const cookieNameMap = {};
      context.cookieRows.forEach(row => {
        cookieNameMap[row.cookieId] = row.cookieName;
      });
      
      // Sort the dimensions rows by cookie name
      return [...prev].sort((a, b) => {
        const nameA = cookieNameMap[a.cookieId] || '';
        const nameB = cookieNameMap[b.cookieId] || '';
        return nameA.localeCompare(nameB);
      });
    });
  };
  
  // Function to get color style for display
  const getColorStyle = (colorName) => {
    // Map color names to CSS colors
    const colorMap = {
      'White': '#ffffff',
      'Black': '#000000',
      'Red': '#ff0000',
      'Blue': '#0000ff',
      'Green': '#00ff00',
      'Yellow': '#ffff00',
      'Orange': '#ffa500',
      'Purple': '#800080',
      'Pink': '#ffc0cb',
      'Brown': '#a52a2a',
      'Gray': '#808080',
      'Teal': '#008080',
      'Navy': '#000080',
      'Maroon': '#800000',
      'Olive': '#808000',
      'Lime': '#00ff00',
      'Aqua': '#00ffff',
      'Fuchsia': '#ff00ff',
      'Silver': '#c0c0c0',
      'Gold': '#ffd700'
    };
    
    // Return the color style
    return {
      backgroundColor: colorMap[colorName] || '#cccccc'
    };
  };
  
  // Function to handle image processing results
  const handleImageProcessed = (width, length, area) => {
    if (context.currentAnalyzingRow) {
      // Update the dimensions row with the processed image data
      updateDimensionsRow(context.currentAnalyzingRow.id, 'width', width.toFixed(2));
      updateDimensionsRow(context.currentAnalyzingRow.id, 'length', length.toFixed(2));
      
      // Close the image analyzer
      context.setShowImageAnalyzer(false);
      context.setCurrentAnalyzingRow(null);
    }
  };
  
  // Return the original context, potentially extended with NEW, NON-OVERRIDING utilities
  return {
    ...context,
    updateDimensionsRow,
    updateColorRow,
    updateColorPercentage,
    addColor,
    removeColor,
    updateOrderRow,
    calculateColorIcing,
    handleUnitToggle,
    handleWeightUnitToggle,
    sortDimensionsRows,
    getColorStyle,
    handleImageProcessed,
    normalizeColorPercentages // Export the new function
  };
};
